import type { TFunction } from "i18next";

interface GenericMetaGridProps {
    meta: any;
    t: TFunction;
}

const myFormatHelper = (key: string, value: any, t: TFunction): React.ReactNode => {
    if (value === null || value === undefined) return '-';

    // Gestione Boolean (isOperational, drinkable, isOccupied, ecc.)
    if (typeof value === 'boolean') {
        return value ? t('common.yes') : t('common.no');
    }

    // Gestione Array (lastDepartureTimes)
    if (Array.isArray(value)) {
        return value.join(', ');
    }

    // Gestione Unità di misura e formati speciali basati sulla chiave
    switch (key) {
        case 'temperature':
            return `${value}°C`;

        case 'currentOccupancy':
        case 'capacity':
            return value.toLocaleString(); // Formatta numeri grandi con separatori

        case 'lastCleaning':
            // Se è una stringa ISO, potresti formattarla con date-fns
            return value; // Esempio: "10:30" o "20 min fa"

        case 'waitingTime':
            return `${value} min`;

        case 'phoneNumber':
            return (
                <a href={`tel:${value}`} className="text-primary underline">
                    {value}
                </a>
            );

        default:
            return String(value);
    }
};

const GenericMetaGridWidget = ({ meta, t }: GenericMetaGridProps) => {
    // Transform the object into an array and filter only existing values
    // Here we don't exclude anything: if a key arrives here, it must be shown.
    const entries = Object.entries(meta).filter(
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        ([_, value]) => value !== null && value !== undefined && value !== ''
    );

    if (entries.length === 0) return null;

    return (
        <div className="grid grid-cols-2 gap-2 mt-4">
            {entries.map(([key, value]) => (
                <div
                    key={key}
                    className="bg-base-200/30 border border-base-300/10 rounded-xl p-3 flex flex-col"
                >
                    <dt className="text-[10px] font-black uppercase tracking-widest opacity-40 leading-tight mb-1">
                        {/* Use a flat translation or the key itself if missing */}
                        {key.replace(/([A-Z])/g, ' $1')}
                    </dt>
                    <dd className="text-sm font-semibold truncate">
                        {myFormatHelper(key, value, t)}
                    </dd>
                </div>
            ))}
        </div>
    );
};

export default GenericMetaGridWidget;